import {Injectable, NgZone} from '@angular/core';
import {NativeService} from './native.service';
import {Observable} from 'rxjs';
import {Device, DeviceInfo} from '@capacitor/device';
import {Capacitor} from '@capacitor/core';
import {ActionPerformed, PushNotifications, Token} from '@capacitor/push-notifications';
import {HttpClient} from '@angular/common/http';
import {SessionService} from './session.service';
import {PushTarget} from '../../shared/models/push-target';
import {Store} from '@ngrx/store';
import {AppState} from '../../app/app.state';
import {PushData} from '../../shared/models/push-notification';
import {Keyboard} from '@capacitor/keyboard';
import {Clipboard} from '@capacitor/clipboard';
import {API_BASE_URL, CURRENT_PLATFORM} from './env.service';
import {AlarmsService} from './alarms.service';
import {StatusBar} from '@capacitor/status-bar';
import {SplashScreen} from '@capacitor/splash-screen';
import {UrlService} from './url.service';

@Injectable({
  providedIn: 'root',
})
export abstract class NativeMobileService extends NativeService {
  protected token: string;

  protected deviceUuid: string;
  protected deviceInfo: DeviceInfo;


  protected constructor(
    protected http: HttpClient,
    protected session: SessionService,
    protected store: Store<AppState>,
    protected alarmsService: AlarmsService,
    protected urlService: UrlService,
    protected ngZone: NgZone,
  ) {
    SplashScreen.show();

    super(http, session, store, alarmsService, urlService, ngZone);

    Device.getId().then((id) => {
      this.deviceUuid = id.identifier;
      return Device.getInfo();
    }).then((info) => {
      this.deviceInfo = info;
      this.afterDeviceInfoLoaded();
    }).then(() => {
      this.store.select('session').subscribe((data) => {
        this.runPushRegistration().subscribe();
      });
    });
  }


  abstract afterDeviceInfoLoaded();


  hideSplash() {
    SplashScreen.hide();
  }


  hideKeyboard() {
    Keyboard.hide();
  }


  clipboardWrite(value: string) {
    Clipboard.write({
      string: value,
    });
  }


  isNativeSmartphone(): boolean {
    return Capacitor.isNativePlatform();
  }


  hideStatusBar() {
    StatusBar.hide();
  }


  showStatusBar() {
    StatusBar.show();
  }


  private registerTarget(token: string) {
    console.log('registerTarget');

    if (token) {
      const body: PushTarget = {
        Type: CURRENT_PLATFORM,
        SelfID: this.deviceUuid,
        SelfToken: token,
        SelfInfo: this.deviceInfo,
      };
      this.http.put<PushTarget>(`${API_BASE_URL}/push/targets`, body).subscribe(
        () => {
          console.log('registered');
        },
        (error) => {
          console.error('registration error');
          console.error(error);
          console.error(body);
        },
      );
    }
  }


  private deregisterTarget(token: string) {
    console.log('deregisterTarget');

    if (token) {
      const params = {
        Type: CURRENT_PLATFORM,
        TargetSelfID: this.deviceUuid,
        TargetSelfToken: token,
      };

      this.http.delete(`${API_BASE_URL}/push/targets`, {
        params: params,
      }).subscribe(
        () => {
          console.log('deregistered');
        },
        (error) => {
          console.error('deregistration error');
          console.error(error);
        },
      );
    }
  }


  runPushRegistration(): Observable<boolean> {
    // On success, we should be able to receive notifications
    PushNotifications.addListener('registration',
      (token: Token) => {
        this.token = token.value;

        if (this.session.userId && !this.session.totpLocked) {
          this.registerTarget(this.token);
        } else {
          this.deregisterTarget(this.token);
        }
      },
    );

    // Some issue with our setup and push will not work
    // PushNotifications.addListener('registrationError',
    //   (error: any) => {
    //     alert('Error on registration: ' + JSON.stringify(error));
    //   }
    // );

    // // Show us the notification payload if the app is open on our device
    // PushNotifications.addListener('pushNotificationReceived',
    //   (notification: PushNotification) => {
    //
    //   }
    // );

    // Method called when tapping on a notification
    PushNotifications.addListener('pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        const pushData: PushData = action.notification.data;
        const uuid: string = pushData.alarmEvent;

        this.routeTapAlarmEventNotification(uuid);
      },
    );

    return new Observable<boolean>((s) => {
      PushNotifications.requestPermissions().then((result) => {
        if (result.receive === 'granted') {
          // Register with Apple/Google to receive push via APNS/FCM
          PushNotifications.register();
        } else {
          // Show some error
        }
      });
    });
  }


  private routeTapAlarmEventNotification(uuid: string) {
    this.ngZone.run(() => {
      this.urlService.navigateByUrl(`/alarms/events/${uuid}`);
    });
  }
}
